import type { FC } from "react";
import { useEffect, useRef } from "react";

import type { MobileDisplayType, StoreLocatorPosition } from "@locators/components/PrizeLocator/PrizeLocator.types";

import { useConfig } from "@jti/app-context/useConfig";
import { SearchDefaultIcon } from "@jti/brand-icons/search-default";
import { UiTargetIcon } from "@jti/brand-icons/ui-target";
import { Button } from "@jti/core-components/components/Button";
import { useGoogleAPI } from "@jti/fe-clients";
import { DEPRECATED_IconButton } from "@jti/ui/components/DEPRECATED_IconButton";
import { InputIcon, TextInput } from "@jti/ui/components/Inputs";
import { useDeviceBreakpoint } from "@jti/ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { replaceStringPlaceholder } from "@jti/ui/utils/replaceStringPlaceholder/replaceStringPlaceholder";

import { usePrizeLocatorContext } from "../../hooks";
import { PrizeLocatorDisplayTypeToggle } from "../PrizeLocatorDisplayTypeToggle";

import styles from "../../PrizeLocator.module.scss";

export type PrizeLocatorSearchBarProps = {
  count: number;
  map?: google.maps.Map;
  loadMarkersFromApi: (map: google.maps.Map) => void;
  onChangeResultType: (type: MobileDisplayType) => void;
  onLoadUserLocationMarker: (position: StoreLocatorPosition) => void;
  hideCount?: boolean;
};

const ENTER_KEY = 13;
const DOWN_ARROW_KEY = 40;

const throwConfigError = () => {
  console.error("Wrong configuration");
  return "";
};

export const PrizeLocatorSearchBar: FC<PrizeLocatorSearchBarProps> = ({
  count,
  map,
  loadMarkersFromApi,
  onChangeResultType,
  onLoadUserLocationMarker,
  hideCount,
}) => {
  const { __ } = useTranslations();
  const _google = useGoogleAPI();
  const config = useConfig();
  const inputRef = useRef<HTMLInputElement>(null);
  const { breakpoint } = useDeviceBreakpoint();
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const {
    labels: { searchHeader, searchBar, searchButton, storesCount: storesCountText, currentPositionButtonText },
  } = usePrizeLocatorContext();

  const options = {
    componentRestrictions: {
      country: config.ISO_COUNTRY_CODE !== undefined ? config.ISO_COUNTRY_CODE : throwConfigError(),
    },
    fields: ["geometry", "name"],
    types: ["geocode", "establishment"],
  };

  useEffect(() => {
    if (inputRef.current && map && _google) {
      autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, options);
      autocompleteRef.current.bindTo("bounds", map);
      autocompleteRef.current.addListener("place_changed", onPlaceChanged);
      pacSelectFirst();
    }
  }, [_google, map]);

  const handleLocationError = (browserHasGeolocation: boolean) => {
    const message = browserHasGeolocation
      ? "Error: The Geolocation service failed."
      : "Error: Your browser doesn't support geolocation.";
    console.error(message);
  };

  const getCurrentLocationGoogleMaps = () => {
    if (navigator.geolocation && map) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          map.setCenter(pos);
          loadMarkersFromApi(map);
          onLoadUserLocationMarker(pos);
        },
        () => {
          handleLocationError(true);
        }
      );
    } else {
      handleLocationError(false);
    }
  };

  const createCustomEvent = (eventName: string, additionalData: { event: string; search_term?: string }) =>
    new CustomEvent(eventName, {
      detail: {
        additionalData,
      },
    });

  const handleSearchForm = (place: google.maps.places.PlaceResult) => {
    const event = createCustomEvent("tracking:event", {
      event: "store_locator_search",
      search_term: inputRef.current?.value,
    });
    document.dispatchEvent(event);
    if (place?.geometry) {
      if (place.geometry.viewport) {
        map?.fitBounds(place.geometry.viewport);
      } else {
        map?.setCenter(place.geometry.location);
      }
    }
  };
  const handleSearchClick = () => {
    const noop = () => null;

    google.maps.event.trigger(inputRef.current, "focus", {});
    google.maps.event.trigger(inputRef.current, "keydown", {
      keyCode: DOWN_ARROW_KEY,
      stopPropagation: noop, // because these get called and throw errors
      preventDefault: noop,
    });
    google.maps.event.trigger(inputRef.current, "keydown", { keyCode: ENTER_KEY });
    google.maps.event.trigger(map, "zoom_changed", {});
  };
  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) {
      return;
    }
    handleSearchForm(place);
  };
  const pacSelectFirst = () => {
    if (inputRef.current) {
      const _addEventListener = inputRef.current.addEventListener;

      const addEventListenerWrapper = (type: string, listener: any) => {
        if (type === "keydown") {
          const originalListener = listener;
          listener = function (event: KeyboardEvent) {
            const suggestionSelected = document.querySelector(".pac-item-selected");
            if (event.which === ENTER_KEY && !suggestionSelected) {
              const simulatedEvent = new KeyboardEvent("keydown", {
                keyCode: DOWN_ARROW_KEY,
                which: DOWN_ARROW_KEY,
              });
              originalListener.apply(inputRef.current, [simulatedEvent]);
            }
            originalListener.apply(inputRef.current, [event]);
          };
        }
        _addEventListener.apply(inputRef.current, [type, listener as EventListenerOrEventListenerObject]);
      };

      inputRef.current.addEventListener = addEventListenerWrapper;
    }
  };

  const storesCount = replaceStringPlaceholder(storesCountText, [{ placeholder: "count", value: count }], false);

  return (
    <div data-testid="storeLocatorSearchBar" className={styles.search}>
      <div className={styles.header}>
        <div className={styles.textContainer} data-testid="text" data-anchor="anchor-text">
          <div className={styles.gridInner} data-testid="PolicyPageInnerText">
            {searchHeader}
          </div>
        </div>
      </div>
      <div className={styles.inputWrapper}>
        <TextInput
          ref={inputRef}
          elemBefore={<InputIcon icon={SearchDefaultIcon} />}
          placeholder={searchBar}
          name="searchInput"
          elemAfter={
            breakpoint === "mobile" ? (
              <InputIcon
                icon={UiTargetIcon}
                onClick={getCurrentLocationGoogleMaps}
                label={__("text_input.default_right_icon_label")}
              />
            ) : undefined
          }
        />
        <Button
          variant="primary"
          onClick={handleSearchClick}
          className={styles.searchButton}
          size={breakpoint === "mobile" ? "sm" : "md"}
        >
          {searchButton}
        </Button>
      </div>
      <div className={styles.bottom}>
        <div className={styles.count}>{!hideCount ? storesCount : null}</div>
        <div className={styles.filtersWrapper}>
          <DEPRECATED_IconButton
            onClick={getCurrentLocationGoogleMaps}
            className={styles.currentLocation}
            icon={UiTargetIcon}
          >
            {currentPositionButtonText}
          </DEPRECATED_IconButton>
        </div>
        <PrizeLocatorDisplayTypeToggle
          name="viewChange"
          id="prizeLocatorSwitch"
          mapLabel={__("storelocator.mobile_view_switch.map")}
          listLabel={__("storelocator.mobile_view_switch.list")}
          onChange={(event) => onChangeResultType(event.target.checked ? "list" : "map")}
        />
      </div>
    </div>
  );
};
